/**
 * RFQ ▸ Award metinleri (ADR 0111 §D): satır bölmesi, ödül kartları, tur geçmişi ve fark
 * vurgusu, kesinleştirme (dört göz), yorumlar. `purchasing.rfq.detail.award` altında.
 */

export const awardEn = {
  title: 'Award',
  intro:
    'Split the RFQ items between suppliers. Each supplier answers its own proposal on the portal: accept, counter-offer or reject. An accepted award is finalized by another approver and becomes a purchase order.',
  loading: 'Loading awards…',
  error: 'The awards could not be loaded.',
  retry: 'Retry',
  split: {
    title: 'Split items',
    note: 'Prices come from the quotations. The best price of each item is selected by default; substitutes are marked.',
    item: 'Item',
    requested: 'Requested',
    supplier: 'Supplier',
    offer: 'Offer',
    qty: 'Qty',
    unitPrice: 'Unit Price',
    total: 'Total',
    none: 'Not awarded',
    noOffers: 'No priced answer',
    awardedTo: 'Awarded · {{supplier}}',
    best: 'Best',
    substitute: 'Substitute',
    locked: '{{supplier}} (finalized)',
    message: 'Message to suppliers (optional)',
    allAwarded: 'Every item is in an award.',
    summary: '{{count}} new items · {{total}}',
    send: 'Send proposal',
    addToAward: 'Add to proposal · Rev {{no}}',
    sent: 'Proposal sent to {{supplier}}.',
  },
  empty: 'No awards yet.',
  emptyText: 'Choose a supplier for each item above and send the proposals.',
  status: {
    draft: 'Draft',
    proposed: 'Awaiting supplier',
    accepted: 'Accepted',
    countered: 'Counter-offer',
    rejected: 'Rejected',
    finalized: 'Finalized',
    cancelled: 'Cancelled',
  },
  round: 'Rev {{no}}',
  // Ofisin satır seçimi (doğrudan ödül; ADR 0166 §2)
  direct: 'Selected by line',
  selectionReason: 'Reason for selection: {{reason}}',
  lines: {
    no: '#',
    item: 'Item',
    qty: 'Qty',
    uom: 'UOM',
    unitPrice: 'Unit Price',
    total: 'Total',
    note: 'Note',
    substituteFor: 'Substitute for {{name}}',
  },
  terms: {
    title: 'Terms',
    leadTimeDays: 'Lead time',
    deliveryLocation: 'Delivery location',
    deliveryTerms: 'Delivery terms',
    paymentTerms: 'Payment terms',
    warranty: 'Warranty',
    discount: 'Discount',
    freight: 'Freight',
    customs: 'Customs',
    tax: 'Tax',
    serviceFee: 'Service fee',
    remarks: 'Remarks',
    itemsTotal: 'Items',
    total: 'Total ({{currency}})',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
  },
  diff: {
    title: 'Changes in Rev {{no}}',
    added: 'Added',
    removed: 'Removed',
    changed: 'Changed',
    qty: 'Qty {{change}}',
    price: 'Price {{change}}',
    substitute: 'Substitute changed',
    note: 'Note changed',
    none: 'No changes.',
    first: 'First proposal',
  },
  history: {
    title: 'Round history',
    party: { office: 'Office', supplier: 'Supplier' },
    action: {
      propose: 'Proposal',
      accept: 'Accepted',
      counter: 'Counter-offer',
      reject: 'Rejected',
      accept_counter: 'Counter-offer accepted',
      select: 'Selected by line',
    },
    hash: 'Content hash',
    by: '{{party}} · {{name}}',
  },
  actions: {
    acceptCounter: 'Accept counter-offer',
    acceptCounterTitle: 'Accept the counter-offer',
    acceptCounterBody:
      'The counter-offer becomes the agreed version. Another approver then finalizes it into a purchase order.',
    propose: 'New proposal',
    finalize: 'Finalize',
    finalizeTitle: 'Finalize the award',
    finalizeBody:
      'This creates the purchase order from the accepted round and sends it to the supplier via the portal.',
    fourEyes: 'Another approver must finalize (four-eyes): you wrote a round of this award.',
    cancel: 'Cancel award',
    cancelTitle: 'Cancel the award',
    cancelBody: 'The items are released and can be awarded again. The rounds stay on record.',
    cancelReason: 'Reason',
    openOrder: 'Open {{no}}',
  },
  proposal: {
    title: 'New proposal · {{supplier}}',
    body: 'Change quantities, prices or terms. The supplier sees every change highlighted.',
    remove: 'Remove',
    message: 'Message (optional)',
    send: 'Send proposal',
    noLines: 'Keep at least one item.',
  },
  documents: {
    title: 'Documents',
    sha: 'SHA-256 {{hash}}',
  },
  comments: {
    title: 'Comments',
    empty: 'No comments yet.',
    immutable: 'Comments cannot be edited or deleted.',
    placeholder: 'Write a comment to the supplier',
    send: 'Send',
    office: 'Office',
    supplier: 'Supplier',
  },
  notices: {
    created: 'Proposal sent to {{supplier}}.',
    proposed: 'New proposal sent to {{supplier}}.',
    accepted: 'Counter-offer of {{supplier}} accepted.',
    finalized: 'Award finalized: purchase order created for {{supplier}}.',
    cancelled: 'Award of {{supplier}} cancelled.',
    commented: 'Comment sent.',
  },
};

export const awardTr: typeof awardEn = {
  title: 'Ödül',
  intro:
    'RFQ kalemlerini tedarikçilere bölün. Her tedarikçi kendi önerisini portalda yanıtlar: kabul, karşı teklif ya da ret. Kabul edilen ödülü başka bir onaycı kesinleştirir ve satın alma siparişi olur.',
  loading: 'Ödüller yükleniyor…',
  error: 'Ödüller yüklenemedi.',
  retry: 'Yeniden dene',
  split: {
    title: 'Kalemleri böl',
    note: 'Fiyatlar tekliflerden gelir. Her kalemin en iyi fiyatı varsayılan seçilir; ikameler işaretlidir.',
    item: 'Kalem',
    requested: 'İstenen',
    supplier: 'Tedarikçi',
    offer: 'Teklif',
    qty: 'Miktar',
    unitPrice: 'Birim Fiyat',
    total: 'Toplam',
    none: 'Ödül yok',
    noOffers: 'Fiyatlı yanıt yok',
    awardedTo: 'Ödülde · {{supplier}}',
    best: 'En iyi',
    substitute: 'İkame',
    locked: '{{supplier}} (kesinleşti)',
    message: 'Tedarikçilere ileti (isteğe bağlı)',
    allAwarded: 'Bütün kalemler bir ödülde.',
    summary: '{{count}} yeni kalem · {{total}}',
    send: 'Öneri gönder',
    addToAward: 'Öneriye ekle · Sürüm {{no}}',
    sent: '{{supplier}} için öneri gönderildi.',
  },
  empty: 'Henüz ödül yok.',
  emptyText: 'Yukarıda her kalem için tedarikçi seçip önerileri gönderin.',
  status: {
    draft: 'Taslak',
    proposed: 'Tedarikçide',
    accepted: 'Kabul edildi',
    countered: 'Karşı teklif',
    rejected: 'Reddedildi',
    finalized: 'Kesinleşti',
    cancelled: 'İptal',
  },
  round: 'Sürüm {{no}}',
  direct: 'Satır satır seçildi',
  selectionReason: 'Seçim gerekçesi: {{reason}}',
  lines: {
    no: '#',
    item: 'Kalem',
    qty: 'Miktar',
    uom: 'Birim',
    unitPrice: 'Birim Fiyat',
    total: 'Toplam',
    note: 'Not',
    substituteFor: '{{name}} yerine ikame',
  },
  terms: {
    title: 'Koşullar',
    leadTimeDays: 'Teslim süresi',
    deliveryLocation: 'Teslim yeri',
    deliveryTerms: 'Teslim şartı',
    paymentTerms: 'Ödeme şartı',
    warranty: 'Garanti',
    discount: 'İndirim',
    freight: 'Navlun',
    customs: 'Gümrük',
    tax: 'Vergi',
    serviceFee: 'Hizmet bedeli',
    remarks: 'Açıklama',
    itemsTotal: 'Kalemler',
    total: 'Toplam ({{currency}})',
    days_one: '{{count}} gün',
    days_other: '{{count}} gün',
  },
  diff: {
    title: 'Sürüm {{no}} değişiklikleri',
    added: 'Eklendi',
    removed: 'Çıkarıldı',
    changed: 'Değişti',
    qty: 'Miktar {{change}}',
    price: 'Fiyat {{change}}',
    substitute: 'İkame değişti',
    note: 'Not değişti',
    none: 'Değişiklik yok.',
    first: 'İlk öneri',
  },
  history: {
    title: 'Tur geçmişi',
    party: { office: 'Ofis', supplier: 'Tedarikçi' },
    action: {
      propose: 'Öneri',
      accept: 'Kabul',
      counter: 'Karşı teklif',
      reject: 'Ret',
      accept_counter: 'Karşı teklif kabul edildi',
      select: 'Satır satır seçildi',
    },
    hash: 'İçerik hash’i',
    by: '{{party}} · {{name}}',
  },
  actions: {
    acceptCounter: 'Karşı teklifi kabul et',
    acceptCounterTitle: 'Karşı teklifi kabul et',
    acceptCounterBody:
      'Karşı teklif üzerinde anlaşılan sürüm olur. Ardından başka bir onaycı onu satın alma siparişine çevirir.',
    propose: 'Yeni öneri',
    finalize: 'Kesinleştir',
    finalizeTitle: 'Ödülü kesinleştir',
    finalizeBody:
      'Kabul edilen turdan satın alma siparişi açılır ve portal üzerinden tedarikçiye gönderilir.',
    fourEyes: 'Başka bir onaycı kesinleştirmeli (dört göz): bu ödülün bir turunu siz yazdınız.',
    cancel: 'Ödülü iptal et',
    cancelTitle: 'Ödülü iptal et',
    cancelBody: 'Kalemler boşalır, yeniden ödüllendirilebilir. Turlar kayıtta kalır.',
    cancelReason: 'Gerekçe',
    openOrder: '{{no}} aç',
  },
  proposal: {
    title: 'Yeni öneri · {{supplier}}',
    body: 'Miktar, fiyat ya da koşulları değiştirin. Tedarikçi her değişikliği vurgulanmış görür.',
    remove: 'Çıkar',
    message: 'İleti (isteğe bağlı)',
    send: 'Öneri gönder',
    noLines: 'En az bir kalem kalmalı.',
  },
  documents: {
    title: 'Belgeler',
    sha: 'SHA-256 {{hash}}',
  },
  comments: {
    title: 'Yorumlar',
    empty: 'Henüz yorum yok.',
    immutable: 'Yorumlar düzeltilemez ve silinemez.',
    placeholder: 'Tedarikçiye yorum yazın',
    send: 'Gönder',
    office: 'Ofis',
    supplier: 'Tedarikçi',
  },
  notices: {
    created: '{{supplier}} için öneri gönderildi.',
    proposed: '{{supplier}} için yeni öneri gönderildi.',
    accepted: '{{supplier}} karşı teklifi kabul edildi.',
    finalized: 'Ödül kesinleşti: {{supplier}} için satın alma siparişi açıldı.',
    cancelled: '{{supplier}} ödülü iptal edildi.',
    commented: 'Yorum gönderildi.',
  },
};

export const awardErrorsEn = {
  LINE_ALREADY_AWARDED: 'An item is already in another award. Reload and choose again.',
  AWARD_EXISTS: 'This supplier already has an award on this RFQ. Send a new proposal to it.',
  STALE_ROUND: 'The award changed meanwhile (a new round arrived). Reload and try again.',
  AWARD_NOT_ACCEPTED: 'Only an accepted award can be finalized.',
  AWARD_FINALIZED: 'A finalized award cannot be cancelled while its purchase order is active.',
  AWARD_CLOSED: 'The award is closed.',
  RFQ_HAS_AWARDS: 'The RFQ has awards. Cancel the awards first.',
  SUPPLIER_NOT_QUOTED: 'Only a supplier that quoted on this RFQ can receive an award.',
  INVALID_AWARD: 'Some award fields are invalid.',
  INVALID_COMMENT: 'Write a comment (up to 2000 characters).',
  COUNTER_LINES_MISMATCH: 'The counter-offer must keep the same items.',
};

export const awardErrorsTr: typeof awardErrorsEn = {
  LINE_ALREADY_AWARDED: 'Bir kalem başka bir ödülde. Yenileyip yeniden seçin.',
  AWARD_EXISTS: 'Bu tedarikçinin bu RFQ’da ödülü var. Ona yeni öneri gönderin.',
  STALE_ROUND: 'Ödül bu arada değişti (yeni tur geldi). Yenileyip yeniden deneyin.',
  AWARD_NOT_ACCEPTED: 'Yalnız kabul edilmiş ödül kesinleştirilir.',
  AWARD_FINALIZED: 'Kesinleşmiş ödül, siparişi etkinken iptal edilemez.',
  AWARD_CLOSED: 'Ödül kapalı.',
  RFQ_HAS_AWARDS: 'RFQ’nun ödülleri var. Önce ödülleri iptal edin.',
  SUPPLIER_NOT_QUOTED: 'Yalnız bu RFQ’ya teklif veren tedarikçiye ödül verilir.',
  INVALID_AWARD: 'Bazı ödül alanları geçersiz.',
  INVALID_COMMENT: 'Bir yorum yazın (en çok 2000 karakter).',
  COUNTER_LINES_MISMATCH: 'Karşı teklif aynı kalemleri taşımalı.',
};
